import React, { useState, useEffect } from 'react';
import { Building2, Plus, X, Factory, Store, Loader2, MapPin, Code, Edit2, Trash2 } from 'lucide-react';
import { branchAPI, fetchPaged, masterChangeAPI } from '../../services/api';
import Pagination from '../ui/Pagination';
import { toast } from 'sonner';

const BranchManager = () => {
  const [branches, setBranches] = useState([]);
  const [stats, setStats] = useState({ total_branches: 0, production_branches: 0, trading_branches: 0 });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };
  const [newBranch, setNewBranch] = useState({
    name: '',
    state: '',
    code: '',
    type: 'Trading',
    is_hq: false
  });
  const [editBranch, setEditBranch] = useState(null);
  const [editForm, setEditForm] = useState({});

  const openBranchEdit = (b) => {
    setEditForm({ name: b.name, gstin: b.gstin || '', address: b.address || '', city: b.city || '', state: b.state || '', pincode: b.pincode || '', type: b.type });
    setEditBranch(b);
  };

  const submitBranchEdit = async () => {
    try {
      await masterChangeAPI.create({ entity_type: 'branch', entity_id: editBranch.id, entity_name: editBranch.name, action: 'edit', changes: editForm, reason: 'Branch update' });
      toast.success('Edit request submitted for Admin approval');
      setEditBranch(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
  };

  const handleBranchDelete = async (b) => {
    const reason = window.prompt(`Reason for deleting branch "${b.name}":`);
    if (reason === null) return;
    try {
      await masterChangeAPI.create({ entity_type: 'branch', entity_id: b.id, entity_name: b.name, action: 'delete', reason });
      toast.success('Delete request submitted for Admin approval');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
  };


  useEffect(() => {
    fetchBranches();
    fetchStats();
  }, [filter, page, pageSize]);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (filter) params.type = filter;
      const result = await fetchPaged('/branches', params);
      setBranches(result.items);
      setTotal(result.total);
      setTotalPages(result.total_pages);
    } catch (err) {
      console.error('Error fetching branches:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await branchAPI.getStats();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleAdd = async () => {
    try {
      setSubmitting(true);
      const newBranchData = await branchAPI.create(newBranch);
      setBranches([newBranchData, ...branches]);
      setShowAdd(false);
      setNewBranch({ name: '', state: '', code: '', type: 'Trading', is_hq: false });
      fetchStats();
    } catch (err) {
      console.error('Error creating branch:', err);
      alert(err.response?.data?.detail || 'Failed to create branch');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredBranches = branches;

  return (
    <div className="p-5 lg:p-6">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Branch Management</h1>
        <p className="text-xs text-gray-500 mt-0.5">Manage production and trading branches across all locations</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e8eef7' }}>
              <Building2 className="w-5 h-5" style={{ color: '#24549c' }} />
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{stats.total_branches}</p>
              <p className="text-[10px] text-gray-500">Total Branches</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
              <Factory className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{stats.production_branches}</p>
              <p className="text-[10px] text-gray-500">Production Units</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
              <Store className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{stats.trading_branches}</p>
              <p className="text-[10px] text-gray-500">Trading Offices</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter & Add */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-2">
          {['', 'Production', 'Trading'].map(f => (
            <button
              key={f}
              onClick={() => { setFilter(f); setPage(1); }}
              className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all ${
                filter === f 
                  ? 'text-white' 
                  : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
              }`}
              style={filter === f ? { backgroundColor: '#24549c' } : {}}
            >
              {f || 'All'}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg hover:shadow-blue-200"
          style={{ backgroundColor: '#24549c' }}
        >
          <Plus className="w-3.5 h-3.5" /> Add Branch
        </button>
      </div>

      {/* Branch List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-sm text-gray-500">Loading branches...</span>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBranches.map(branch => (
            <div key={branch.id} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  {branch.type === 'Production' ? (
                    <Factory className="w-4 h-4 text-purple-600" />
                  ) : (
                    <Store className="w-4 h-4 text-green-600" />
                  )}
                  <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                    branch.type === 'Production' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {branch.type}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {branch.is_hq && (
                    <span className="inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full bg-blue-100 text-blue-700 mr-1">HQ</span>
                  )}
                  <button onClick={() => openBranchEdit(branch)} data-testid={`edit-branch-${branch.id}`}
                    className="p-1 rounded hover:bg-blue-50" title="Request Edit">
                    <Edit2 className="w-3 h-3 text-blue-500" />
                  </button>
                  <button onClick={() => handleBranchDelete(branch)} data-testid={`delete-branch-${branch.id}`}
                    className="p-1 rounded hover:bg-red-50" title="Request Delete">
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </button>
                </div>
              </div>
              
              <h3 className="text-sm font-bold text-gray-900 mb-2" style={{ fontFamily: 'DM Sans' }}>
                {branch.name}
              </h3>
              
              <div className="space-y-1.5 text-[11px] text-gray-500">
                <p className="flex items-center gap-1.5">
                  <MapPin className="w-3 h-3" style={{ color: '#24549c' }} />
                  {branch.city ? `${branch.city}, ${branch.state}` : branch.state}
                </p>
                <p className="flex items-center gap-1.5">
                  <Code className="w-3 h-3" style={{ color: '#24549c' }} />
                  Code: <span className="font-mono font-semibold text-gray-700">{branch.code}</span>
                </p>
                {branch.gstin && (
                  <p className="flex items-center gap-1.5">
                    <span className="w-3 h-3 text-center text-[8px] font-bold" style={{ color: '#24549c' }}>G</span>
                    <span className="font-mono text-[10px] text-gray-600">{branch.gstin}</span>
                  </p>
                )}
                {branch.is_temporary && (
                  <span className="inline-flex px-2 py-0.5 text-[9px] font-semibold rounded-full bg-orange-100 text-orange-600">Temporary Site</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {branches.length > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />}

      {/* Add Branch Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-base font-bold" style={{ fontFamily: 'DM Sans' }}>New Branch</h2>
              <button onClick={() => setShowAdd(false)} className="p-1 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Branch Name</label>
                <input
                  value={newBranch.name}
                  onChange={e => setNewBranch({ ...newBranch, name: e.target.value })}
                  placeholder="e.g., Mumbai HQ - Production Unit"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">State</label>
                  <input
                    value={newBranch.state}
                    onChange={e => setNewBranch({ ...newBranch, state: e.target.value })}
                    placeholder="Maharashtra"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Branch Code</label>
                  <input
                    value={newBranch.code}
                    onChange={e => setNewBranch({ ...newBranch, code: e.target.value.toUpperCase() })}
                    placeholder="MH-PROD"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Branch Type</label>
                <select
                  value={newBranch.type}
                  onChange={e => setNewBranch({ ...newBranch, type: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
                >
                  <option value="Trading">Trading (Sales & Distribution)</option>
                  <option value="Production">Production (Manufacturing)</option>
                </select>
                <p className="text-[10px] text-gray-500 mt-1">
                  {newBranch.type === 'Production' 
                    ? '⚙️ Can create production orders and handle manufacturing'
                    : '🏪 Sales and distribution only, no production'
                  }
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_hq"
                  checked={newBranch.is_hq}
                  onChange={e => setNewBranch({ ...newBranch, is_hq: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-1 focus:ring-blue-200"
                />
                <label htmlFor="is_hq" className="text-xs text-gray-700">Mark as Headquarters</label>
                <input
                  type="checkbox"
                  id="is_temp"
                  checked={newBranch.is_temporary || false}
                  onChange={e => setNewBranch({ ...newBranch, is_temporary: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-1 focus:ring-orange-200 ml-4"
                />
                <label htmlFor="is_temp" className="text-xs text-gray-700">Temporary Site</label>
              </div>

              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">GSTIN</label>
                <input
                  value={newBranch.gstin || ''}
                  onChange={e => setNewBranch({ ...newBranch, gstin: e.target.value.toUpperCase() })}
                  placeholder="e.g. 20AAPCS1562H1ZG"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">City</label>
                  <input
                    value={newBranch.city || ''}
                    onChange={e => setNewBranch({ ...newBranch, city: e.target.value })}
                    placeholder="Ranchi"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Pincode</label>
                  <input
                    value={newBranch.pincode || ''}
                    onChange={e => setNewBranch({ ...newBranch, pincode: e.target.value })}
                    placeholder="835103"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Address</label>
                <input
                  value={newBranch.address || ''}
                  onChange={e => setNewBranch({ ...newBranch, address: e.target.value })}
                  placeholder="Full address"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
              <button
                onClick={() => setShowAdd(false)}
                disabled={submitting}
                className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={submitting}
                className="px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg hover:shadow-blue-200 disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: '#24549c' }}
              >
                {submitting ? <><Loader2 className="w-3 h-3 animate-spin" /> Adding...</> : 'Add Branch'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Branch Modal */}
      {editBranch && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setEditBranch(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-base font-bold" style={{ fontFamily: 'DM Sans' }}>Edit Branch: {editBranch.name}</h2>
              <button onClick={() => setEditBranch(null)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <p className="mx-5 mt-3 text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Changes will be submitted for Admin approval.</p>
            <div className="p-5 space-y-3">
              {[
                { key: 'name', label: 'Name' }, { key: 'gstin', label: 'GSTIN' },
                { key: 'address', label: 'Address' }, { key: 'city', label: 'City' },
                { key: 'state', label: 'State' }, { key: 'pincode', label: 'Pincode' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-[11px] font-medium text-gray-600 mb-1 block">{f.label}</label>
                  <input value={editForm[f.key] || ''} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
              ))}
              <div>
                <label className="text-[11px] font-medium text-gray-600 mb-1 block">Type</label>
                <select value={editForm.type} onChange={e => setEditForm(p => ({ ...p, type: e.target.value }))}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2">
                  <option value="Production">Production</option>
                  <option value="Trading">Trading</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => setEditBranch(null)} className="px-4 py-2 text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={submitBranchEdit} data-testid="submit-branch-edit"
                className="px-4 py-2 text-[11px] font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>Submit for Approval</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchManager;
